import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import logo from '../../assets/img/illustrations/falcon.png';
import { invitationService } from './service';
import './AcceptInvitation.css';

const MIN_PASSWORD_LENGTH = 8;
const ROLE_LABELS = { owner: 'Group Owner', admin: 'Group Admin' };

// Invite link for corporate Group Owners/Admins: verify the invite, set a password, then sign in.
const GroupInvitation = () => {
  const token = new URLSearchParams(window.location.search).get('token');
  const [status, setStatus] = useState('loading'); // loading | form | success | error
  const [message, setMessage] = useState('');
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const verify = async () => {
    if (!token) {
      setStatus('error');
      setMessage('This invitation link is incomplete. Please ask for a new invitation.');
      return;
    }
    setStatus('loading');
    const response = await invitationService.verifyGroupInvitation(token);
    if (response.success && response.data) {
      setInvite(response.data);
      setStatus('form');
    } else {
      setStatus('error');
      setMessage(response.message);
    }
  };

  useEffect(() => {
    verify();
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!invite.has_account) {
      if (password.length < MIN_PASSWORD_LENGTH) {
        setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      if (password !== confirmPassword) {
        setFormError('Passwords do not match.');
        return;
      }
    }
    setFormError('');
    setSubmitting(true);
    const response = await invitationService.acceptGroupInvitation(token, invite.has_account ? undefined : password);
    setSubmitting(false);
    if (response.success) {
      setStatus('success');
      setMessage(
        invite.has_account
          ? 'Invitation accepted. Sign in with your existing password and choose this group account.'
          : 'Your account is ready. Sign in with your email and new password.'
      );
    } else {
      setFormError(response.message);
    }
  };

  return (
    <div className="invitation-page">
      <div className="invitation-card" role="status" aria-live="polite">
        <img className="invitation-logo" src={logo} alt="Gizmeon" />

        {status === 'loading' && (
          <>
            <span className="invitation-spinner" aria-label="Loading" />
            <h1>Checking your invitation</h1>
            <p>Please wait...</p>
          </>
        )}

        {status === 'form' && invite && (
          <form onSubmit={handleSubmit} className="text-left">
            <h1 className="text-center">Join {invite.group_name}</h1>
            <p className="text-center">
              {invite.publisher_name ? `${invite.publisher_name} has` : "You've been"} invited you to manage the corporate
              membership group <strong>{invite.group_name}</strong> as {ROLE_LABELS[invite.role] || 'Group Admin'}.
            </p>

            <div className="form-group">
              <label>Email</label>
              <input className="form-control" type="email" value={invite.email} disabled />
            </div>

            {invite.has_account ? (
              <p>You already have an account with this email. Accept the invitation, then sign in with your existing password.</p>
            ) : (
              <>
                <div className="form-group">
                  <label>Create password</label>
                  <input
                    className="form-control"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <small className="text-500">At least {MIN_PASSWORD_LENGTH} characters.</small>
                </div>
                <div className="form-group">
                  <label>Confirm password</label>
                  <input
                    className="form-control"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                  />
                </div>
              </>
            )}

            {formError && <p style={{ color: 'red', fontStyle: 'Italic', marginBottom: '1rem' }}>{formError}</p>}

            <div className="text-center">
              <button className="invitation-primary-action" type="submit" disabled={submitting}>
                {submitting ? 'Please wait...' : invite.has_account ? 'Accept and sign in with your existing password' : 'Create account'}
              </button>
            </div>
          </form>
        )}

        {status === 'success' && (
          <>
            <FaCheckCircle className="invitation-status-icon invitation-status-success" aria-hidden="true" />
            <h1>Invitation accepted</h1>
            <p>{message}</p>
            <Link className="invitation-primary-action" to={{ pathname: '/login', state: { email: invite && invite.email } }}>
              Go to sign in
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <FaExclamationCircle className="invitation-status-icon invitation-status-error" aria-hidden="true" />
            <h1>Unable to accept invitation</h1>
            <p>{message}</p>
            <Link className="invitation-secondary-action" to="/login">
              Go to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default GroupInvitation;
